/* eslint-disable react/self-closing-comp */
import dynamic from 'next/dynamic';
import Link from 'next/link';
import styled from 'styled-components';
import { rgba } from 'polished';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Account } from 'layout/header/components/Account';
import { AnimatedSearchSuggestions } from 'search/components/search/AnimatedSearchSuggestions';
import { BREADCRUMBS } from 'breadcrumbs/constants/breadcrumbs';
import { Banner } from 'banner/components/Banner';
import { DESKTOP_BANNER_HEIGHT, MOBILE_BANNER_HEIGHT } from 'banner/enums/constants';
import {
  DESKTOP_HEADER_HEIGHT,
  DESKTOP_HIDE_HEADER_HEIGHT,
  MOBILE_HEADER_HEIGHT,
  NEW_NAV_MOBILE_HEADER_HEIGHT,
  NEW_NAV_MOBILE_HIDE_HEADER_HEIGHT,
} from 'layout/header/enums/constants';
import { FeatureFlags } from 'featureFlags/constants/featureFlags';
import { GoatLogo } from 'shared/icons';
import { HeaderNavigationDesktop } from 'layout/header/components/HeaderNavigationDesktop';
import { ICMSBreadcrumb } from 'shared/types/Breadcrumbs';
import { mockListingsData } from 'layout/header/__mocks__/mockListingsData';
import { MobileNavigationPanel } from 'layout/header/components/MobileNavigationPanel';
import { PageLayoutActions } from 'layout/state/pageLayoutReducer';
import { SearchBarDesktop } from 'search/components/search/SearchBarDesktop';
import { SearchPanelMobile } from 'search/components/search/SearchPanelMobile';
import { UnorderedList, KebabMenu } from 'shared/components';
import { variableFontRegular } from 'styles/theme';
import { sendTrackingEvent } from 'tracking/utils';
import { useAccountModalState } from 'account/hooks/useAccountModal';
import {
  useBanner,
  useBreadcrumbs,
  useFeatureFlag,
  useWindowSize,
  usePageLayoutDispatch,
  usePageLayoutState,
} from 'shared/hooks';
import { toggleVisible } from 'styles/theme/animation';
import { SizePreferencesModalProvider } from 'sizePreferences/providers/SizePreferencesModalProvider';
import { useRouter } from 'next/router';
import { AVAILABLE_LOCALES, LOCALES } from '../../../localization/constants/configEnums';
import { useSitePreferencesModal } from '../../../shared/hooks/useSitePreferencesModal';

const LogInCreateAccountModal = dynamic(
  () => import('account/components/LogInCreateAccountModal'),
  { ssr: false },
);

const ForgotPasswordModal = dynamic(() => import('account/components/ForgotPasswordModal'), {
  ssr: false,
});

const ResetPasswordModal = dynamic(() => import('account/components/ResetPasswordModal'), {
  ssr: false,
});

const Header = ({ headerItemsCount }: { headerItemsCount: number }) => {
  const dispatch = usePageLayoutDispatch();
  const navBreadcrumbs = useBreadcrumbs(BREADCRUMBS.NAVIGATION);
  const headerLinks: ICMSBreadcrumb[] =
    navBreadcrumbs?.children || mockListingsData.navigation.children;
  const calendarBreadcrumbs = useBreadcrumbs(BREADCRUMBS.CALENDAR);
  const calendarLinks: ICMSBreadcrumb[] =
    calendarBreadcrumbs?.children || mockListingsData.calendar.children;
  const showBlackFridayLink = useFeatureFlag(FeatureFlags.WEB_ENABLE_BLACK_FRIDAY_IN_NAV);
  const { isHeaderVisible } = usePageLayoutState();
  const { title, subtitle, isBannerEnabled } = useBanner();
  const isBannerVisible = isBannerEnabled && (title || subtitle);

  /*
   * To keep snapshots consistent, adding this state so that
   * if cypress is running we display the same amount of inventory to
   * snapshots consistent
   */
  const [isCypress, setIsCypress] = useState(false);

  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const { isDesktop } = useWindowSize();
  const { modalContext } = useAccountModalState();
  const mobileNavPanelBodyRef = useRef<HTMLDivElement>();
  const { openSitePreferencesModal } = useSitePreferencesModal();

  const { locale } = useRouter();
  const [showInternationalToaster, setShowInternationalToaster] = useState(false);
  const [userClicked, setUserClicked] = useState(false);

  useEffect(() => {
    const handleClicked = () => setUserClicked(true);
    document.addEventListener('click', handleClicked);

    if (locale !== LOCALES.enUS) {
      setShowInternationalToaster(true);
    }

    return () => {
      document.removeEventListener('click', handleClicked);
    };
  }, [locale]);
  const handleToasterClick = () => {
    openSitePreferencesModal();
    sendTrackingEvent('USER_REGION_TOASTER_TAP', { region: AVAILABLE_LOCALES[locale].country });
    setShowInternationalToaster(false);
  };

  const { t } = useTranslation();

  const isLogInCreateAccountModalOpen =
    modalContext === 'logIn' || modalContext === 'createAccount';

  const isForgotPasswordModalOpen = modalContext === 'forgotPassword';

  const isResetPasswordModalOpen = modalContext === 'resetPassword';

  const openMobileNavMenu = () => {
    setIsMobileNavOpen(true);
    dispatch({ type: PageLayoutActions.setIsMenuVisible, payload: true });
    sendTrackingEvent('MORE_MENU_VIEW');
  };

  const closeMobileNavMenu = () => {
    setIsMobileNavOpen(false);
    dispatch({ type: PageLayoutActions.setIsMenuVisible, payload: false });
  };

  const openMobileSearch = () => {
    setIsMobileSearchOpen(true);
  };

  const closeMobileSearch = () => setIsMobileSearchOpen(false);

  useEffect(() => {
    if (isMobileNavOpen) {
      closeMobileNavMenu();
    }

    // @ts-ignore cypress sets this window value
    if (window?.Cypress) {
      setIsCypress(true);
    }
  }, [isDesktop]);

  return (
    <SizePreferencesModalProvider>
      <Header.Container $isHeaderVisible={isHeaderVisible} $isBannerVisible={isBannerVisible}>
        {/* below div used by Branch.io to insert "download app" banner without overlapping nav */}
        <div className="branch-journeys-top"></div>
        {isBannerVisible && <Banner />}
        <Header.Wrapper $showBF={showBlackFridayLink}>
          <Header.SearchDesktop>
            <SearchBarDesktop />
          </Header.SearchDesktop>
          <Header.Navigation>
            <Header.NavigationList>
              <HeaderNavigationDesktop
                data-qa="header_nav_home"
                href="/"
                label={t('layout.navigation.home', 'Home')}
              />
              <HeaderNavigationDesktop
                data-qa="header_nav_listings"
                menuItems={headerLinks}
                href="/search"
                label={`${t(
                  'layout.navigation.shopNumOfItems',
                  'Shop {numOfItems, plural, =1 {1 Item} other {# Items}}',
                  {
                    numOfItems: isCypress ? 255084 : headerItemsCount,
                  },
                )}`}
              />
              <HeaderNavigationDesktop
                data-qa="header_nav_editorial"
                href="/editorial"
                label={t('layout.header.editorial', 'Editorial')}
              />
              <HeaderNavigationDesktop
                data-qa="header_nav_calendar"
                menuItems={calendarLinks}
                href="/timeline"
                label={t('layout.header.timeline', 'Timeline')}
              />
            </Header.NavigationList>
          </Header.Navigation>

          <MobileNavigationPanel
            calendarLinks={calendarLinks}
            closeMobileNavMenu={closeMobileNavMenu}
            headerItemsCount={headerItemsCount}
            headerLinks={headerLinks}
            isMobileNavOpen={isMobileNavOpen}
            mobileNavPanelBodyRef={mobileNavPanelBodyRef}
          />
          <Header.Logo>
            <Link href="/" passHref prefetch={false}>
              <a aria-label="GOAT logo" data-qa="goat_logo">
                <Header.GoatLogo width={38} height={9} />
              </a>
            </Link>
          </Header.Logo>
          {showBlackFridayLink && (
            <Header.Link aria-label="Link to GOAT Black Friday Page" href="/blackfriday">
              BF22
            </Header.Link>
          )}
          <Header.SearchMobile
            $isBlackFridayLinkVisible={showBlackFridayLink}
            onClick={openMobileSearch}
            data-qa="mobile_search"
          >
            {t('layout.header.search', 'Search')}
            <AnimatedSearchSuggestions />
          </Header.SearchMobile>

          {isMobileSearchOpen && <SearchPanelMobile closeSearch={closeMobileSearch} />}
          <Header.RightSideContainer>
            <Header.LeftInvisibleElementOfRightContainer />
            <Header.CenterRightSide>
              <Header.InternationalToaster
                onClick={handleToasterClick}
                $isVisible={showInternationalToaster}
                $userClicked={userClicked}
              >
                <span>
                  Your shopping region is <u>{AVAILABLE_LOCALES[locale].countryName}</u>. Applicable
                  import costs and VAT included.
                </span>
              </Header.InternationalToaster>
              <Header.BlackFridayLinkContainer>
                {showBlackFridayLink && isDesktop && (
                  <HeaderNavigationDesktop
                    data-qa="header_nav_black_friday"
                    href="/blackfriday"
                    label={t('layout.navigation.blackFriday', 'Black Friday 2022')}
                  />
                )}
              </Header.BlackFridayLinkContainer>
            </Header.CenterRightSide>
            <Header.Account>
              <Account />
            </Header.Account>
          </Header.RightSideContainer>
          <Header.Menu onClick={openMobileNavMenu} data-qa="nav_menu">
            <Header.KebabMenu />
          </Header.Menu>
          {isLogInCreateAccountModalOpen && (
            <LogInCreateAccountModal isOpen={isLogInCreateAccountModalOpen} />
          )}
          {isForgotPasswordModalOpen && <ForgotPasswordModal isOpen={isForgotPasswordModalOpen} />}
          {isResetPasswordModalOpen && <ResetPasswordModal isOpen={isResetPasswordModalOpen} />}
        </Header.Wrapper>
      </Header.Container>
      {/* Spacer is to maintain the header height since header has fixed position */}
      {isHeaderVisible && <Header.Spacer $isBannerVisible={isBannerVisible} />}
    </SizePreferencesModalProvider>
  );
};

export default Header;

const DESKTOP_HEADER_AND_BANNER_HEIGHT = `calc(${DESKTOP_HEADER_HEIGHT} + ${DESKTOP_BANNER_HEIGHT})`;
const MOBILE_HEADER_AND_BANNER_HEIGHT = `calc(${MOBILE_HEADER_HEIGHT} + ${MOBILE_BANNER_HEIGHT})`;
const NEW_NAV_MOBILE_HEADER_AND_BANNER_HEIGHT = `calc(${
  NEW_NAV_MOBILE_HEADER_HEIGHT as string
} + ${MOBILE_BANNER_HEIGHT})`;

Header.Container = styled.header<{
  $isHeaderVisible: boolean;
  $isBannerVisible: boolean;
}>`
  position: fixed;
  left: 0;
  right: 0;
  top: ${({ $isHeaderVisible, $isBannerVisible }) => {
    if ($isBannerVisible) {
      return $isHeaderVisible
        ? `0`
        : `calc(${NEW_NAV_MOBILE_HIDE_HEADER_HEIGHT} - ${MOBILE_BANNER_HEIGHT})`;
    }
    return $isHeaderVisible ? `0` : NEW_NAV_MOBILE_HIDE_HEADER_HEIGHT;
  }};

  ${({ $isBannerVisible }) =>
    `height: ${$isBannerVisible ? MOBILE_HEADER_AND_BANNER_HEIGHT : NEW_NAV_MOBILE_HEADER_HEIGHT};
     transition: all 0.3s ease-in;`}
  background: ${({ theme }) => theme.navigation.headerBackgroundColor};
  border-bottom: 1px solid ${({ theme }) => theme.navigation.mainBorderColor};
  z-index: ${({ theme }) => theme.zIndex.nav};

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumLarge}px) {
    top: ${({ $isHeaderVisible, $isBannerVisible }) => {
      if ($isBannerVisible) {
        return $isHeaderVisible
          ? `0`
          : `calc(${DESKTOP_HIDE_HEADER_HEIGHT} - ${DESKTOP_BANNER_HEIGHT})`;
      }
      return $isHeaderVisible ? `0` : DESKTOP_HIDE_HEADER_HEIGHT;
    }};
    height: ${({ $isBannerVisible }) =>
      $isBannerVisible ? DESKTOP_HEADER_AND_BANNER_HEIGHT : DESKTOP_HEADER_HEIGHT};
  }

  [data-qa='header_nav_black_friday'] {
    text-decoration: underline !important;
  }
`;

Header.Wrapper = styled.header<{ $showBF?: boolean }>`
  align-items: center;
  display: flex;
  justify-content: space-between;

  padding: 20px 0px;
  ${variableFontRegular({ size: '11.5px' })};
  padding: 14px 16px;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumLarge}px) {
    padding: 12px 32px 0;
    ${variableFontRegular({ size: '11.5px', height: '14px' })};
  }
`;

Header.Spacer = styled.header<{ $isBannerVisible: boolean }>`
  background: none;
  top: 0;
  position: sticky;
  height: ${({ $isBannerVisible }) =>
    $isBannerVisible ? NEW_NAV_MOBILE_HEADER_AND_BANNER_HEIGHT : NEW_NAV_MOBILE_HEADER_HEIGHT};
  z-index: ${({ theme }) => theme.zIndex.appLandingNav};

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumLarge}px) {
    height: ${({ $isBannerVisible }) =>
      $isBannerVisible ? DESKTOP_HEADER_AND_BANNER_HEIGHT : DESKTOP_HEADER_HEIGHT};
  }
`;

Header.Menu = styled.div`
  display: flex;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumLarge}px) {
    display: none;
  }
`;

Header.KebabMenu = styled(KebabMenu)`
  ${KebabMenu.Dot} {
    background-color: ${({ theme }) => theme.navigation.textColor};
  }
`;

Header.Logo = styled.div`
  height: 100%;
  text-align: center;
  display: flex;
  align-items: center;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumLarge}px) {
    display: none;
    padding-top: 11px;
    place-self: start;
  }
`;

Header.GoatLogo = styled(GoatLogo)`
  fill: ${({ theme }) => theme.navigation.textColor} !important;
`;

Header.SearchDesktop = styled.div`
  display: none;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumLarge}px) {
    display: block;
  }
`;

Header.Navigation = styled.nav`
  position: relative;
  display: none;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumLarge}px) {
    display: flex;
    position: absolute;
    left: 0;
    right: 0;
    margin: auto;
    justify-content: center;
    white-space: nowrap;
  }
`;

Header.NavigationList = styled(UnorderedList)`
  display: flex;
`;

Header.Link = styled.a`
  align-items: center;
  display: flex;
  flex-direction: column;
  font-size: 11px;
  justify-content: center;
  line-height: 13px;
  margin-left: 16px;
  text-decoration: underline !important;
  text-transform: uppercase;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumLarge}px) {
    display: none;
  }
`;

Header.Account = styled(UnorderedList)`
  display: none;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumLarge}px) {
    display: block;

    [data-qa='account_header'] {
      margin-bottom: 1px;
    }
  }
`;

Header.SearchMobile = styled.button<{ $isBlackFridayLinkVisible: boolean }>`
  border: 0;
  cursor: pointer;
  display: flex;
  min-width: ${({ $isBlackFridayLinkVisible }) =>
    $isBlackFridayLinkVisible ? `calc(100vw - 180px)` : `calc(100vw - 142px)`};

  :hover {
    text-decoration: underline;
  }

  text-align: left;
  border: 1px solid ${({ theme }) => rgba(theme.navigation.textColor, 0.7)};
  margin-left: 16px;
  margin-right: 4px;
  padding: 5px 10px;

  :hover {
    text-decoration: none;
  }

  :focus,
  :active {
    border: 1px solid ${({ theme }) => rgba(theme.navigation.textColor, 0.7)};
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumLarge}px) {
    display: none;
  }
`;

Header.MobileNavItem = styled.button`
  cursor: pointer;
  padding: 0;

  :hover {
    text-decoration: underline;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumLarge}px) {
    display: none;
  }
`;

Header.MobileNavLink = styled.a`
  cursor: pointer;
  padding: 0;

  :hover {
    text-decoration: underline;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumLarge}px) {
    display: none;
  }
`;

Header.MobileLinks = styled.div`
  display: flex;
  justify-content: space-evenly;
  width: 100%;
  min-width: 100vw;
`;

Header.RightSideContainer = styled.div`
  @media (max-width: ${({ theme }) => theme.breakpoints.mediumLarge}px) {
    display: none !important;
  }

  display: flex;
  justify-content: space-between;
  width: calc(
    50vw - 173px
  ); // 173 is the width the center of the right minus the width that is taken up by the middle unordered list and padding
`;

Header.LeftInvisibleElementOfRightContainer = styled.div``;

Header.CenterRightSide = styled.div`
  position: relative;
`;

Header.BlackFridayLinkContainer = styled.div`
  bottom: 0;
  left: 0;
  margin: auto;
  width: 109px;
  position: absolute;
  right: 0;
  top: 0;
`;

Header.InternationalToaster = styled.div<{ $isVisible: boolean; $userClicked: boolean }>`
  background-color: ${({ theme }) => theme.navigation.backgroundColor};
  display: flex;
  cursor: pointer;
  margin: 6px 0px 9px 0px;
  flex-wrap: nowrap;
  font-size: 9px;
  line-height: 11px;
  padding: 4px;
  width: 360px;
  position: relative;
  opacity: 0;
  display: none;
  visibility: hidden;
  white-space: nowrap;
  ${({ $isVisible }) => $isVisible && toggleVisible};
  ${({ $userClicked }) =>
    $userClicked &&
    `transform: translateY(-100%);
    visibility: hidden;
    opacity: 0;
    transition: all 0.5s ease-out;
  `};
  animation: slideIn 0.5s ease-in;
  border: ${({ theme }) => theme.navigation.border};
  z-index: ${({ theme }) => theme.zIndex.nav};

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumLarge}px) {
    display: flex;
    align-content: center;
    justify-content: center;
  }

  @keyframes slideIn {
    0% {
      transform: translateY(-100%);
    }
    100% {
      transform: translateY(0%);
    }
  }
`;
